/**
 * Carril [A] · PR2 — Tour PROGRESIVO (modelo elegido por Arturo).
 * En vez de un bloque de ~9 pasos al primer login, mostramos UN coach-mark la
 * primera vez que el usuario entra a cada sección. Se aprende en contexto, sin
 * barrera de arranque. Nunca bloquea: se cierra con "Entendido", Esc, X u overlay.
 *
 * "Visto" se recuerda en localStorage POR USUARIO (preferencia local, sin columna
 * en base). El tour completo re-lanzable (guidedTour) es aparte; "Reiniciar
 * consejos" limpia estos flags para volver a verlos.
 */
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { helpEntry, type HelpMe } from './helpContent';

type DriverInstance = ReturnType<typeof driver>;

const keyFor = (userId?: string) => `stratos.coach.seen.${userId ?? 'anon'}`;

function readSeen(userId?: string): Set<string> {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (raw) return new Set(JSON.parse(raw));
  } catch { /* corrupto/ausente */ }
  return new Set();
}
function writeSeen(userId: string | undefined, set: Set<string>): void {
  try { localStorage.setItem(keyFor(userId), JSON.stringify([...set])); } catch { /* quota */ }
}
export function hasSeenCoach(userId: string | undefined, key: string): boolean {
  return readSeen(userId).has(key);
}
export function markCoachSeen(userId: string | undefined, key: string): void {
  const s = readSeen(userId); s.add(key); writeSeen(userId, s);
}
/** Vuelve a habilitar todos los coach-marks progresivos para este usuario. */
export function resetCoachSeen(userId?: string): void {
  try { localStorage.removeItem(keyFor(userId)); } catch { /* noop */ }
}

// Un solo coach a la vez; y no lo lancemos encima del tour completo.
let active: DriverInstance | null = null;
export function coachIsActive(): boolean { return !!active; }

/**
 * Muestra el coach-mark de una sección si aplica y no se ha visto. Devuelve true
 * si lo mostró. Se marca "visto" al mostrarlo (aunque lo cierren sin leer: no lo
 * repetimos — es re-obtenible desde el Centro de ayuda / "Reiniciar consejos").
 */
export function showSectionCoach(me: HelpMe, key: string, userId?: string): boolean {
  if (active) return false;
  const entry = helpEntry(key);
  if (!entry || !entry.show(me) || hasSeenCoach(userId, key)) return false;

  // Ancla al item del sidebar si está visible; si no (drawer móvil cerrado),
  // mostramos el coach centrado (sin elemento) para no apuntar a algo oculto.
  const el = document.querySelector<HTMLElement>(`[data-tour="nav-${key}"]`);
  const visible = !!el && el.offsetParent !== null;

  const d = driver({
    allowClose: true,
    overlayOpacity: 0.5,
    stagePadding: 6,
    doneBtnText: 'Entendido',
    showButtons: ['next', 'close'],
    onDestroyed: () => { active = null; },
    steps: [{
      element: visible ? `[data-tour="nav-${key}"]` : undefined,
      popover: {
        title: entry.title,
        description: `${entry.coach}<br><br><span style="opacity:.6;font-size:12px">Vuelve a ver todos los consejos en Ayuda › ¿Cómo funciona?</span>`,
        ...(visible ? { side: 'right' as const, align: 'start' as const } : {}),
      },
    }],
  });
  markCoachSeen(userId, key); // no repetir aunque lo cierren
  active = d;
  d.drive();
  return true;
}
